import { create } from 'zustand';
import type { CartItem, CartWithTotals } from '@armory-shop/shared';
import {
  getCart,
  addToCart as addToCartApi,
  updateCartItem as updateCartItemApi,
  removeCartItem as removeCartItemApi,
  clearCart as clearCartApi,
} from '@/shared/api/cart';

interface CartState {
  items: CartItem[];
  totals: Omit<CartWithTotals, 'items'> | null;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchCart: () => Promise<void>;
  addItem: (productId: number, quantity?: number) => Promise<void>;
  updateQuantity: (itemId: number, quantity: number) => Promise<void>;
  removeItem: (itemId: number) => Promise<void>;
  clear: () => Promise<void>;
  reset: () => void;
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],
  totals: null,
  isLoading: false,
  error: null,

  fetchCart: async () => {
    set({ isLoading: true, error: null });
    try {
      const cart = await getCart();
      set({
        items: cart.items,
        totals: {
          originalTotal: cart.originalTotal,
          itemsDiscountAmount: cart.itemsDiscountAmount,
          subtotal: cart.subtotal,
          orderDiscountPercent: cart.orderDiscountPercent,
          orderDiscountAmount: cart.orderDiscountAmount,
          totalFinal: cart.totalFinal,
          appliedDiscountType: cart.appliedDiscountType,
        },
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch cart',
        isLoading: false,
      });
    }
  },

  addItem: async (productId, quantity = 1) => {
    set({ isLoading: true, error: null });
    try {
      const item = await addToCartApi(productId, quantity);
      set((state) => ({
        items: [...state.items, item],
        isLoading: false,
      }));
      // Refetch to get updated totals
      await get().fetchCart();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to add item',
        isLoading: false,
      });
    }
  },

  updateQuantity: async (itemId, quantity) => {
    set({ isLoading: true, error: null });
    try {
      const item = await updateCartItemApi(itemId, quantity);
      set((state) => ({
        items: state.items.map((i) => (i.id === itemId ? item : i)),
        isLoading: false,
      }));
      // Refetch to get updated totals
      await get().fetchCart();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update quantity',
        isLoading: false,
      });
    }
  },

  removeItem: async (itemId) => {
    set({ isLoading: true, error: null });
    try {
      await removeCartItemApi(itemId);
      set((state) => ({
        items: state.items.filter((i) => i.id !== itemId),
        isLoading: false,
      }));
      // Refetch to get updated totals
      await get().fetchCart();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to remove item',
        isLoading: false,
      });
    }
  },

  clear: async () => {
    set({ isLoading: true, error: null });
    try {
      await clearCartApi();
      set({ items: [], totals: null, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to clear cart',
        isLoading: false,
      });
    }
  },

  reset: () => {
    set({ items: [], totals: null, isLoading: false, error: null });
  },
}));
